/**
 * Triveny.ai — left rail navigation.
 *
 * Replaces SidebarNavigation entirely for Triveny.ai users
 * (config.isTriveny === true). WeaveIn users continue to use the legacy
 * SidebarNavigation untouched.
 *
 * Implements surface #0 ("the rail") from design_handoff_platform_redesign:
 *   - 210px fixed-width, --v2-ink background, flex column
 *   - Triveny wordmark (top, white) — text fallback until /triveny-wordmark-white.svg ships
 *   - "My day" home item (sun icon)
 *   - 4 grouped sections with mono UPPERCASE labels: Design, Market, Engage, Operate
 *   - User chip pinned to bottom (avatar circle + name + role)
 *
 * Item visual states (per design):
 *   resting → rgba(255,255,255,.62) text on ink bg
 *   hover   → rgba(255,255,255,.06) bg + white text
 *   active  → rgba(255,255,255,.10) bg + white text + accent-bright count
 *
 * The active-item highlight comes from matching the item's tab ID against
 * `activeTab`. The activeTab → menu-item mapping mirrors how SidebarNavigation
 * highlights its rows.
 *
 * Props shape mirrors SidebarNavigation so the swap in app.tsx is a one-liner.
 */

import React, { useMemo } from 'react';
import {
  Home,
  LayoutTemplate,
  Send,
  Users,
  BarChart3,
  Target,
  Filter,
  PhoneOutgoing,
  Brain,
  DollarSign,
  Headphones,
  UserPlus,
  Bot,
  LogOut,
  Settings as SettingsIcon,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';

// ----------------------------------------------------------------------------
// Tokens — reference --v2-* CSS variables already in globals.css.
// Keep symbols identical to TrivenyMyDay.tsx for consistency.
// ----------------------------------------------------------------------------

const tokens = {
  // ink: HARDCODED dark hex (not var(--v2-ink)). The CSS variable inverts to
  // light gray (#e5e7eb) under `.dark` mode, which would turn the rail bg
  // light and wash out the white text on it. The rail is a fixed dark
  // surface by design, independent of system/app dark mode.
  ink: '#14171c',
  accent: 'var(--v2-accent)',
  accentBright: '#4cc4f0', // hex per design handoff (accent-bright on dark)
  rSm: 'var(--v2-r-sm)',
  fontSans: "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  fontDisplay: "'Inter Tight', 'Inter', -apple-system, system-ui, sans-serif",
  fontMono: "'JetBrains Mono', ui-monospace, Menlo, monospace",
};

// ----------------------------------------------------------------------------
// Nav map — single source of truth for what the rail shows + which tab each
// item navigates to. Tab IDs match the activeTab values used in app.tsx and
// listed in TRIVENY_ALLOWED_TABS. Items whose tab isn't in TRIVENY_ALLOWED_TABS
// today are still listed (they'll just be hidden by the existing redirect
// useEffect until the tab is implemented).
// ----------------------------------------------------------------------------

type RailItem = {
  label: string;
  icon: React.ComponentType<{ size?: number; color?: string; style?: React.CSSProperties }>;
  /** activeTab value to set when clicked */
  tab: string;
  /** Optional count badge (e.g. unread inbox messages) */
  count?: string | number;
};

type RailGroup = {
  /** Mono UPPERCASE group label */
  label: string;
  items: RailItem[];
};

// Per dashboard.jsx → TvRail mockup (design_handoff_platform_redesign).
// Item→tab mapping uses the existing platform-* tab IDs from app.tsx where
// they exist; new tabs map to the closest existing surface with TODOs.
//
// Home is rendered as a top-level item ABOVE these groups in the component
// body (it routes to activeTab='triveny-home', the full-bleed launcher).
// My Day + Inbox are NOT in the rail any more — both are launched from the
// Triveny home top nav per user direction.
export const RAIL_GROUPS: RailGroup[] = [
  {
    label: 'Design',
    items: [
      // 'Studio' renamed from 'Landing pages' — surface covers landing pages,
      // emails, and forms (anything the AI generator can produce).
      { label: 'Studio', icon: LayoutTemplate, tab: 'platform-web-presence' },
    ],
  },
  {
    label: 'Market',
    items: [
      { label: 'Campaigns', icon: Send, tab: 'platform-marketing' },
      // TODO: 'platform-audiences' — closest existing is platform-contacts (contact lists)
      { label: 'Audiences', icon: Users, tab: 'platform-contacts' },
      // Outreach = the unified workspace that tabs across Visitor ID
      // (inbound), Lead Builder (ICP + outbound list), Cold Outreach
      // (sequences), and a cross-channel Funnel view. Renders
      // OutreachWorkspace; the three legacy sub-routes remain reachable
      // as deep links and redirect into the matching tab.
      { label: 'Funnel', icon: Filter, tab: 'platform-outreach' },
    ],
  },
  {
    label: 'Engage',
    items: [
      // Engage is pipeline management. Intelligence (intent/signals) moved into
      // the Outreach workspace — it's a top-of-funnel marketing surface, not
      // pipeline. platform-intelligence still resolves for deep links.
      { label: 'Pipeline', icon: Target, tab: 'platform-sales' },
    ],
  },
  {
    label: 'Operate',
    items: [
      { label: 'RevOps', icon: DollarSign, tab: 'platform-revops' },
      { label: 'Agents', icon: Bot, tab: 'platform-agents' },
      { label: 'Support', icon: Headphones, tab: 'platform-support' },
      { label: 'Team', icon: UserPlus, tab: 'platform-team' },
    ],
  },
];

// activeTab IDs that should highlight the "Home" row at the top of the rail.
// triveny-home is the full-bleed launcher; legacy 'home' and the My Day tab
// (platform-dashboard) light it up too so the user always sees their anchor
// when they're on a "landing" surface.
const HOME_TABS = new Set(['triveny-home', 'platform-dashboard', 'home']);

// ----------------------------------------------------------------------------
// Inline style helpers (mirrors .tv-rail .* classes in
// design_handoff_platform_redesign/designs/redesign.css)
// ----------------------------------------------------------------------------

const itemBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 9px',
  borderRadius: tokens.rSm,
  fontFamily: tokens.fontSans,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  transition: 'background 75ms ease, color 75ms ease',
};

function itemStyle(isActive: boolean): React.CSSProperties {
  return {
    ...itemBaseStyle,
    background: isActive ? 'rgba(255,255,255,0.10)' : 'transparent',
    color: isActive ? '#ffffff' : 'rgba(255,255,255,0.62)',
  };
}

// Section headers match the nav-link typography (Inter 13px) instead of the
// old 9px mono caps — they read as colored labels, not muted eyebrows. Each
// header takes one of the three brand spark colors from the logo mark
// (teal/blue/violet). Operate has no 4th brand color, so it stays neutral.
const GROUP_COLORS: Record<string, string> = {
  Design: '#009a83', // teal spark
  Market: '#46b1e5', // blue spark
  Engage: '#7468ae', // violet spark
  Operate: 'rgba(255,255,255,0.55)', // neutral — no 4th brand color
};
const GROUP_COLOR_FALLBACK = 'rgba(255,255,255,0.55)';

const groupLabelStyle: React.CSSProperties = {
  fontFamily: tokens.fontSans,
  fontSize: 13,
  fontWeight: 600,
  padding: '14px 9px 5px',
};

function countStyle(isActive: boolean): React.CSSProperties {
  return {
    marginLeft: 'auto',
    fontFamily: tokens.fontMono,
    fontSize: 10,
    fontWeight: 600,
    color: isActive ? tokens.accentBright : 'rgba(255,255,255,0.5)',
  };
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export interface TrivenyRailProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  /** Called when the user clicks the home item. */
  onHomeClick?: () => void;
  /** Display name for the user chip at the bottom of the rail. */
  currentUser?: { id?: number | string; name?: string | null; email?: string | null } | null;
  /** Free-text role label rendered below the user name when no tenantName is given. */
  userRoleLabel?: string;
  /** Tenant / organization name rendered under the user name in the bottom chip.
      When provided, takes precedence over userRoleLabel so the user always sees
      which org they're currently logged into. */
  tenantName?: string | null;
  /** Open the profile/account drawer when the user chip is clicked. */
  onShowProfile?: () => void;
  /** Logout handler — surfaces a small icon-only logout button next to the chip. */
  onLogout?: () => void;
  /** Optional unread counts (used by the rail badges). */
  counts?: {
    inbox?: number;
    calls?: number;
  };
  /** When true (mobile drawer), close-on-tap behavior should be triggered by the parent. */
  onCloseMobile?: () => void;
  /** Collapsed (icon-only) rail on Triveny desktop. */
  collapsed?: boolean;
  /** Toggle the collapsed state (renders a chevron button at the top). */
  onToggleCollapse?: () => void;
  /** Cmd/Ctrl+click or middle-click on an item: open that page in a new tab of this window. */
  onOpenInNewTab?: (tab: string) => void;
}

function userInitials(user: TrivenyRailProps['currentUser']): string {
  if (!user) return '?';
  const name = (user.name || '').trim();
  if (name) {
    const parts = name.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase();
  }
  const email = (user.email || '').trim();
  return email ? email[0].toUpperCase() : '?';
}

export default function TrivenyRail({
  activeTab,
  onTabChange,
  onHomeClick,
  currentUser,
  userRoleLabel,
  tenantName,
  onShowProfile,
  onLogout,
  counts,
  onCloseMobile,
  collapsed,
  onToggleCollapse,
  onOpenInNewTab,
}: TrivenyRailProps) {
  const isHomeActive = HOME_TABS.has(activeTab);

  // Inject runtime counts into the rail (e.g. Inbox unread). Memoize so item
  // identity is stable across renders when counts don't change.
  const groups = useMemo<RailGroup[]>(() => {
    if (!counts) return RAIL_GROUPS;
    return RAIL_GROUPS.map((g) => ({
      ...g,
      items: g.items.map((it) => {
        // 'Funnel' (formerly 'Outreach' / 'Calls') still reports a count when
        // provided — unread/queued cold-outreach items in the dialer.
        if (it.label === 'Funnel' && typeof counts.calls === 'number') {
          return { ...it, count: counts.calls > 0 ? String(counts.calls) : undefined };
        }
        return it;
      }),
    }));
  }, [counts]);

  const handleItemClick = (tab: string) => {
    onTabChange(tab);
    onCloseMobile?.();
  };

  return (
    <div
      style={{
        width: collapsed ? 64 : 210,
        flexShrink: 0,
        background: tokens.ink,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        padding: collapsed ? '16px 8px' : '16px 12px',
        gap: 4,
        height: '100%',
        overflowY: 'auto',
        // Hide the default scrollbar visually but keep it functional
        scrollbarColor: 'rgba(255,255,255,0.12) transparent',
      }}
      className="triveny-rail"
      aria-label="Triveny navigation"
    >
      {/* Wordmark — brand-supplied SVG with white text + colored spark.
          Used directly without any CSS filter inversion; the dedicated
          white variant renders the actual brand spark palette (teal,
          blue, violet) instead of an inverted approximation. */}
      {/* The wordmark links out to the PUBLIC marketing site at the apex
          (triveny.ai). The app lives on app.triveny.ai; clicking the logo
          takes the user "home" to the public site. Computed from the current
          host so dev (dev.triveny.ai) and prod both resolve to their apex. */}
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            alignSelf: collapsed ? 'center' : 'flex-end',
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.55)',
            cursor: 'pointer',
            padding: 6,
            borderRadius: 6,
            display: 'inline-flex',
            marginBottom: collapsed ? 8 : -4,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#fff';
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'rgba(255,255,255,0.55)';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}
      {!collapsed && (
      <a
        href={
          'https://' +
          ((typeof window !== 'undefined' ? window.location.hostname : '')
            .replace(/^(app|dev)\./, '') || 'triveny.ai')
        }
        title="Go to triveny.ai"
        aria-label="Triveny home"
        // Left-aligned to the nav content indent (9px) so the wordmark's left
        // edge lines up with the item icons + section headers below.
        style={{ alignSelf: 'flex-start', margin: '4px 9px 18px', display: 'inline-flex', lineHeight: 0 }}
      >
        <img
          src="/triveny/logo-white.svg"
          alt="triveny"
          style={{ height: 24, width: 'auto', maxWidth: 180, display: 'block' }}
          onError={(e) => {
            // Fallback: render the inline text wordmark if the PNG is missing.
            const fallback = document.createElement('div');
            fallback.style.cssText =
              'height:19px;font-family:' +
              tokens.fontDisplay +
              ';font-size:15px;font-weight:700;letter-spacing:-0.02em;color:#fff;display:flex;align-items:center;';
            fallback.innerHTML =
              'triveny<span style="color:' + tokens.accentBright + '">.ai</span>';
            const parent = (e.target as HTMLImageElement).parentNode;
            if (parent) parent.replaceChild(fallback, e.target as Node);
          }}
        />
      </a>
      )}

      {/* Home — top item, routes to triveny-home (the full-bleed launcher).
          My Day and Inbox are launched from inside TrivenyHome's top nav per
          user direction (they're not in the sidebar anymore). */}
      <button
        type="button"
        style={{ ...itemStyle(isHomeActive), ...(collapsed ? { justifyContent: 'center' } : {}) }}
        onClick={() => {
          if (onHomeClick) onHomeClick();
          else handleItemClick('triveny-home');
        }}
        onMouseEnter={(e) => {
          if (!isHomeActive) {
            e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
            e.currentTarget.style.color = '#fff';
          }
        }}
        onMouseLeave={(e) => {
          if (!isHomeActive) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'rgba(255,255,255,0.62)';
          }
        }}
        aria-current={isHomeActive ? 'page' : undefined}
        aria-label="Home"
        title="Home"
      >
        <Home size={15} />
      </button>

      {/* Grouped sections */}
      {groups.map((group) => (
        <React.Fragment key={group.label}>
          {!collapsed && (
            <div style={{ ...groupLabelStyle, color: GROUP_COLORS[group.label] ?? GROUP_COLOR_FALLBACK }}>
              {group.label}
            </div>
          )}
          {group.items.map((item) => {
            const isActive = activeTab === item.tab && !isHomeActive;
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                type="button"
                title={collapsed ? item.label : undefined}
                style={{ ...itemStyle(isActive), ...(collapsed ? { justifyContent: 'center' } : {}) }}
                onClick={(e) => {
                  if (onOpenInNewTab && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    onOpenInNewTab(item.tab);
                    return;
                  }
                  handleItemClick(item.tab);
                }}
                onAuxClick={(e) => {
                  if (e.button !== 1 || !onOpenInNewTab) return;
                  e.preventDefault();
                  onOpenInNewTab(item.tab);
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.color = '#fff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'rgba(255,255,255,0.62)';
                  }
                }}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={15} />
                {!collapsed && <span>{item.label}</span>}
                {!collapsed && item.count !== undefined && <span style={countStyle(isActive)}>{item.count}</span>}
              </button>
            );
          })}
        </React.Fragment>
      ))}

      {/* Home-page continuity: the launcher's boat image fills the empty space
          below the nav groups. A top-down gradient that starts at the exact
          rail color (#14171c) fades into the photo so there's no hard seam
          between the nav sections and the image. Negative horizontal margin
          bleeds it to the rail edges (cancels the 12px container padding).
          flex:1 makes it absorb whatever vertical space is left above the
          user chip; it shrinks to minHeight on short viewports. */}
      {collapsed && <div style={{ flex: 1 }} />}
      {!collapsed && (
      <div
        aria-hidden="true"
        style={{
          flex: '1 1 auto',
          minHeight: 96,
          // Negative margins bleed the image past the container's padding on
          // all three open sides (left/right 12px, bottom 16px) so the boat
          // reaches the rail edges with no black gutter below it.
          margin: '12px -12px -16px',
          backgroundImage:
            "linear-gradient(to bottom, #14171c 0%, rgba(20,23,28,0.92) 24%, rgba(20,23,28,0.5) 100%), url('/triveny/home-bg.jpg')",
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundPosition: 'top center, center bottom',
          backgroundSize: 'auto, cover',
          pointerEvents: 'none',
        }}
      />
      )}

      {/* User name + sign-out moved to the top-right user menu (TvPillarBar)
          to match the home page. The boat backdrop above now runs to the
          bottom of the rail. */}
    </div>
  );
}

/**
 * Why a separate component (not a fork of SidebarNavigation):
 *  - SidebarNavigation is 1400+ lines: capability filtering, org switcher, search,
 *    keyboard shortcuts, breadcrumb-driven highlighting, collapsed/expanded modes,
 *    notification bell, theme toggle, sync indicator. Triveny needs none of that.
 *  - The Triveny design is intentionally minimal — fixed width, grouped items,
 *    no collapse, no search inline. Forking SidebarNavigation would mean carrying
 *    1400 lines of code we'd strip out one block at a time.
 *  - Keeping SidebarNavigation untouched means zero regression risk for the
 *    WeaveIn experience.
 *
 * The unused imports (SettingsIcon, ChevronRight) are intentionally kept — they
 * are referenced in the design mockup's future Settings / Operate items that
 * aren't yet exposed as tabs.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
void SettingsIcon;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
void ChevronRight;
